/* eslint-disable react/no-array-index-key */
import React from 'react';
import { useImmer } from 'use-immer';
import styled from 'styled-components';
import { type CFMobile, type CFMobileItem, ImageType } from '@types';
import addComponent from '@utils/addComponent';
import { sendWarning } from '@components/Noti';
import { Collapse } from '@components/Collapse';
import Button from '@components/Button';
import Tip from '@components/Tip';
import Title from '@components/Title';
import Image from '@components/Image';
import useInputHandlers from '@hooks/useInputHandelrs';
import useToggle from '@hooks/useToggle';
import { Validator } from '@components/Validator';
import Modal from '@components/Modal';
import Input from '@components/Input';
import SortableList from '@components/SortableList';
import arrayMove from '@utils/arrayMove';
import { flexibleMobileTest } from '@utils/mobileTest';
import { selectLibImages } from '../../../mediaLib';
import { useCustomFeature } from '../../CustomFeatureContext';
import ImgNoAvatarLg from './img-no-avatar-lg.png';
import CMobileItem from './CMobileItem';

const MAX = 10;
const LENGTH = 50;

function CMobileSetting() {
  const { data, setData } = useCustomFeature<CFMobile>();
  const { mobiles } = data;

  const setMobile = (index: number) => {
    addComponent((remove) => (
      <ContactModal
        defaultValue={mobiles[index]}
        onRequestClose={remove}
        onConfirm={(contract) =>
          setData((draft) => {
            draft.mobiles[index] = contract;
          })
        }
      />
    ));
  };

  return (
    <Collapse
      title={
        <div className="flex">
          <div>电话设置</div>
        </div>
      }
    >
      <Button
        className="w-full"
        disabled={mobiles.length >= MAX}
        onClick={() => setMobile(mobiles.length)}
      >
        创建联系人
      </Button>
      <Validator
        value={mobiles}
        validFn={(value) => !!value.length}
        warningMessage="请添加电话号码"
      >
        {({ value }) => (
          <>
            <div className="mt-20 mb-10 flex">
              <div>联系人列表：</div>
              <div className="ml-10 self-end text-black-gray">{`${mobiles.length}/${MAX}`}</div>
            </div>
            {!value.length && (
              <>
                <Tip className="text-base">还未创建电话联系人</Tip>
                <Tip className="text-base">{`最多可创建${MAX}个电话号码`}</Tip>
              </>
            )}
            <SortableList
              axis="y"
              lockAxis="y"
              distance={10}
              onSortEnd={(sort) => {
                setData((draft) => {
                  draft.mobiles = arrayMove({
                    array: draft.mobiles,
                    fromIndex: sort.oldIndex,
                    toIndex: sort.newIndex,
                  });
                });
              }}
            >
              <div>
                {value.map((x, i) => (
                  <CMobileItem
                    data={x}
                    key={i}
                    index={i}
                    itemIndex={i}
                    onClick={setMobile}
                    onDelete={(index) => {
                      setData((draft) => {
                        draft.mobiles.splice(index, 1);
                      });
                    }}
                  />
                ))}
              </div>
            </SortableList>
          </>
        )}
      </Validator>
    </Collapse>
  );
}

const StyledModal = styled(Modal)`
  width: 800px;
  height: 550px;
`;

const StyledInput = styled(Input)`
  width: 400px;
  height: 40px;
`;

function ContactModal({
  defaultValue,
  onConfirm,
  onRequestClose,
}: {
  defaultValue?: CFMobileItem;
  onConfirm: (contract: CFMobileItem) => void;
  onRequestClose: () => void;
}) {
  const warning = useToggle(false);
  const [state, setState] = useImmer(() => {
    if (defaultValue) {
      return {
        ...defaultValue,
        mobile: defaultValue.mobile.toString(),
      };
    }
    return {
      name: '',
      mobile: '',
      avatarMediaId: 0,
      avatarMediaUrl: '',
    };
  });

  const nameHandlers = useInputHandlers((val) =>
    setState((draft) => {
      draft.name = val;
    })
  );

  const mobileHandlers = useInputHandlers((val) =>
    setState((draft) => {
      draft.mobile = val;
    })
  );

  const handleConfirm = () => {
    if (!flexibleMobileTest(state.mobile.replace(/，/g, ','))) {
      sendWarning('请输入正确的电话号码');
      warning.on();
      return;
    }
    onConfirm({
      ...state,
      mobile: state.mobile.replace(/，/g, ','),
    });
    onRequestClose();
  };

  return (
    <StyledModal
      isOpen
      header={<Title>创建联系人</Title>}
      footer={
        <Button disabled={!state.name || !state.mobile} onClick={handleConfirm}>
          确认操作
        </Button>
      }
      onRequestClose={onRequestClose}
    >
      <div className="flex h-full items-center justify-center">
        <div className="flex justify-center">
          <div className="flex flex-col items-center">
            <div className="flex h-100 w-100 items-center justify-center overflow-hidden rounded bg-white-border">
              {state.avatarMediaUrl ? (
                <Image
                  className="h-full w-full"
                  src={state.avatarMediaUrl}
                  type={ImageType.media}
                />
              ) : (
                <img src={ImgNoAvatarLg} className="h-40 w-40" alt="" />
              )}
            </div>
            <Button
              className="mt-10 w-100"
              onClick={async () => {
                const { libItems } = await selectLibImages();
                if (!libItems) return;
                setState((draft) => {
                  draft.avatarMediaId = libItems[0].id;
                  draft.avatarMediaUrl = libItems[0].url;
                });
              }}
            >
              设置头像
            </Button>
            <Tip className="mt-10">尺寸:100 x100</Tip>
          </div>
          <div className="ml-20 flex flex-col">
            <StyledInput
              maxLength={LENGTH}
              placeholder={`请输入姓名/称呼，不超过${LENGTH}个字符`}
              value={state.name}
              {...nameHandlers}
            />
            <StyledInput
              maxLength={LENGTH}
              className="mt-20"
              placeholder={`请输入电话号码，不超过${LENGTH}个字符`}
              value={state.mobile}
              warning={warning.isOn}
              {...mobileHandlers}
              onChange={(e) => {
                warning.off();
                mobileHandlers.onChange(e);
              }}
            />

            <div className="mt-20 flex text-black-gray">
              提示：分机号请使用英文逗号“,”隔开，如：4008886666,1234
            </div>
          </div>
        </div>
      </div>
    </StyledModal>
  );
}

export default CMobileSetting;
